import { Component, Input, Output, EventEmitter, OnInit, Inject, ViewChild } from '@angular/core';
import { FormGroup, Validators, FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material';
import { each, set, cloneDeep, isEmpty, pick, isNil, get, isArray, isString, clone } from 'lodash-es';
import { BehaviorSubject } from 'rxjs';

import { NotificationService } from '../../../../../services/notification.service';
import { LocalStorageService } from '../../../../../services/local-storage.service';
import { SmsService } from '../../../../../services/sms.service';
import { FileService } from '../../../../../services/file/file.service';
import { EmailService } from '../../../../../services/email.service';
import { Select } from '../../../../../objects/select';
import { QuillEditorComponent } from 'ngx-quill';
import { LooseObject } from '../../../../../objects/loose-object';
import { WorkflowContentUsableField } from '../../../../../admin/workflow/types';
import { FieldmagicDropdownOption } from '../../../../../shared/components/forms/input/dropdown/dropdown.component';
import { RecordService } from '../../../../../services/record.service';
import { TranslateService } from '@ngx-translate/core';

import {
  EMAIL_RELATED_MODULE_CONTENT_USABLE_FIELD_ALIASES,
  EMAIL_RELATED_MODULE_CONTENT_USABLE_FIELDS,
  EMAIL_EXCLUDED_MODULE_FIELDS,
  EMAIL_PER_MODULE_EXCLUDED_COMPARISON_FIELD_TYPES
} from '../../../../../admin/email-template/constants';

import {
  blank,
  data_get,
  fallback,
  filled,
  spf,
  str_singular
} from '../../../../../shared/utils/common';
import { ViewService } from '../../../../../services/view.service';
import { first } from 'rxjs/operators';

@Component({
  selector: 'fieldmagic-related-field',
  templateUrl: './related-field.component.html',
  styleUrls: [
    './related-field.component.scss',
  ]
})
export class FieldmagicRelatedFieldComponent {
  @Input() form: FormGroup;
  @Input() moduleOptions: any[] = [];
  @Input() fieldOptions: FieldmagicDropdownOption<WorkflowContentUsableField>[] = [];
  @Input() moduleLabel: string = 'module';
  @Input() bindValue?: string;
  @Input() fieldLabel: string = 'field';
  @Input() grouped: boolean = true;
  @Input() variableMapping: LooseObject = {};
  @Input() isOpen: boolean = false;
  @Output() moduleChange = new EventEmitter<any>();
  @Output() fieldChange = new EventEmitter<LooseObject>();
  @Output() closeVar = new EventEmitter<LooseObject>();
  @Input() moduleName: string = '';


  public objAvailableModules: any;
  arCustomAttributes: string[] = [];
  public strModule: string = null;
  arModuleFields: Array<LooseObject>;

  public lastFocusedInput: HTMLInputElement | HTMLTextAreaElement | 'quill' | null = null;

  constructor(
      private fileService: FileService,
      private smsService: SmsService,
      private notificationService: NotificationService,
      private recordService: RecordService,
      private localStorageService: LocalStorageService,
      @Inject(MAT_DIALOG_DATA) private data: ConfigureEmailDialogProps,
      private emails: EmailService,
      private dialog: MatDialog,
      private viewService: ViewService,
      protected translator: TranslateService,
    ) {

      let emailTemplate = (this.data["id"]) ? cloneDeep(this.data) : {};
      this.initializeComponent(emailTemplate);
  }
  public strField: string = null;
  public strVariable: string = null;
  public emailTemplateForm: FormGroup;
  readonly fields$ = new BehaviorSubject<FieldmagicDropdownOption<WorkflowContentUsableField>[]>([]);
  public modules$: any[] = [];
  @ViewChild("quill") quill: QuillEditorComponent;

  @ViewChild('dropdownInput')
  dropdownInput: any;

  public emailTemplateField = {
    name: {
      key: "name",
      controlType: "text",
      type: "text",
      label: "name",
      max_length: 128,
      required: true,
      default_value: null
    },
    subject: {
      key: "subject",
      controlType: "text",
      type: "text",
      label: "subject",
      max_length: 128,
      required: false,
      default_value: null
    },
    body: {
      key: "body",
      controlType: "textarea",
      type: "textarea",
      label: "body",
      required: false,
      default_value: null
    },
    module: {
      key: "module",
      controlType: "dropdown",
      type: "dropdown",
      label: "module",
      required: false,
      default_value: null,
      clearable: true,
      options: []
    },
    field: {
      key: "field",
      controlType: "dropdown",
      type: "dropdown",
      label: "field",
      required: false,
      default_value: null,
      options: []
    },
    share_template: {
      key: 'share_template',
      controlType: 'checkbox',
      type: 'checkbox',
      label: 'share_template',
      required: false,
      default_value: false
    }
  };

  ngOnInit() {
    this.initModules();
    if( this.moduleName === 'opportunities' && this.variableMapping ) {
      this._buildFieldsFromVariableMapping();
    } else {
      this.loadModule(new Select(this.moduleName, this.moduleName));
    }
  }

  private _buildFieldsFromVariableMapping(): void {
    const options: FieldmagicDropdownOption<WorkflowContentUsableField>[] = [];

    Object.keys(this.variableMapping).forEach(key => {
      let groupName = 'Quote';
      let displayText = key;

      if (key.startsWith('assigned_')) {
        groupName = 'User';
        displayText = key.replace('assigned_', '');
      } else if (key.startsWith('contact')) {
        groupName = 'Contact';
        displayText = key.replace('contact_', '');
      } else if (key.startsWith('customer')) {
        groupName = 'Customer';
        displayText = key.replace('customer_', '');
      } else if (key.startsWith('site')) {
        groupName = 'Site';
        displayText = key.replace('site_', '');
      }

      options.push({
        id: key,
        text: displayText,
        type: 'text',
        module: this.moduleName,
        groupName: groupName,
      });
    });

    const currentFields = this.fields$.value || [];
    this.fields$.next([...currentFields, ...options]);
  }

  /**
   * Initialize the component with the provided data.
   * @param data - The data to initialize the component with.
   */
  initializeComponent(data: object): void {
    this.emailTemplateForm = new FormGroup({
      name: new FormControl(data["name"] ? data["name"] : null, Validators.required),
      subject:  new FormControl(data["subject"] ? data["subject"] : null),
      attachment: new FormControl(data["attachment"] ? data["attachment"] : null),
      body: new FormControl(data["body"] ? data["body"] : null),
      module: new FormControl(data["module"] ? data["module"] : null),
      field: new FormControl(null),
      share_template: new FormControl(get(data, 'share_template', false))
    });
  }

  initModules(): void {
    const ALLOWED_CUSTOM_FIELD_TYPE = ['number', 'text', 'currency', 'textarea', 'date', 'datetime'];
    this.objAvailableModules = this.localStorageService.getJsonItem('sms_related_modules');

    if (isNil(this.objAvailableModules)) {
      this.smsService.getRelatedMetadata().subscribe(metadataList => {
        if (metadataList) {
          let smsRelatedFields = {};

          Object.keys(metadataList).forEach((module) => {
            smsRelatedFields[module] = [];
            Object.keys(metadataList[module]).forEach((field) => {
              if (metadataList[module][field].hasOwnProperty('in_sms_template') ||
                ALLOWED_CUSTOM_FIELD_TYPE.some(allowedfield => field.includes(allowedfield))) {
                smsRelatedFields[module].push({ field: field, label : metadataList[module][field]['label']});
              }
            });
          });

          this.objAvailableModules = smsRelatedFields;
          this.localStorageService.setJsonItem('sms_related_modules', smsRelatedFields);
        }
      });
    }
  }

  public clearDropdownSelection(): void {
    if (this.dropdownInput) {
      this.dropdownInput.value = null;
      if (this.dropdownInput.$input) {
        this.dropdownInput.$input.clearModel();
      }
    }
  }

  onInputFocus(event: FocusEvent): void {
    this.lastFocusedInput = event.target as HTMLInputElement | HTMLTextAreaElement;
  }

  loadModule(objEvent: Select): void {
    this.moduleName = objEvent['text'] || objEvent['value'];
    if(filled(this.data['record_data']) && this.data['record_data'].is_supplier){
      this.moduleName = 'supplier_invoices';
    }
    this.recordService.getFields(this.moduleName).subscribe(response => {
      // Get excluded fields for the current module
        const perModuleExcludedFields = fallback(data_get(EMAIL_PER_MODULE_EXCLUDED_COMPARISON_FIELD_TYPES, this.moduleName), {
              fallback: () => [],
            });

        this.arModuleFields = Object.keys(response).filter(field =>
          !EMAIL_EXCLUDED_MODULE_FIELDS.includes(field) &&
          !perModuleExcludedFields.includes(field)
        )
        .map(field => {
          if (this.moduleName === 'purchase_orders' && (field === 'site_id' || field === 'warehouse_id')) {
            let strLabel = (field === 'site_id') ? 'delivery_to_sites' : 'delivery_to_warehouse';
            return new Select(field, strLabel, null, response[field]);
          } else if (this.moduleName === 'assets' && field === 'asset_type_variant_id') {
            const strLabel = 'asset_type_variant_text';
            return new Select(strLabel, field, null, response[field]);
          } else {
            return new Select(field, response[field].label, null, response[field]);
          }
        }).filter(selectField => !perModuleExcludedFields.includes(selectField.id));
        this.arModuleFields.unshift(new Select('id', `${str_singular(this.translator.instant(this.moduleName))} ID`, null, {}));
        if(['leads','contacts','customers','sites'].includes(this.moduleName)) {
          this.arModuleFields.push(new Select('email_address', 'email_address', null, {}));
          this.arModuleFields.push(new Select('address', 'address', null, {}));
          this.arModuleFields.push(new Select('phone', 'phone', null, {}));
        }
        this._buildUsableContentFields(this.arModuleFields);
    });
  }

  generateModuleFields(moduleData: LooseObject): void {
    this.strModule = moduleData.value;
    // clear the fields first
    this.emailTemplateField.field.options = [];
    this.emailTemplateForm.patchValue({ field: null });
    this.strField = null;

    let arModuleFields: ModuleField[] = this.objAvailableModules[this.strModule];

    if (this.strModule === 'purchase_orders') {
      let numDeliverToSiteIndex: number = arModuleFields.findIndex(moduleField => moduleField.field === 'site_id');
      let numDeliverToWarehouseIndex: number = arModuleFields.findIndex(moduleField => moduleField.field === 'warehouse_id');

      if (numDeliverToSiteIndex > -1) {
        arModuleFields[numDeliverToSiteIndex]['label'] = 'delivery_to_sites';
      }

      if (numDeliverToWarehouseIndex > -1) {
        arModuleFields[numDeliverToWarehouseIndex]['label'] = 'delivery_to_warehouse';
      }
    }

    if (arModuleFields) {
      this.emailTemplateField.field.options = arModuleFields.map(moduleField => new Select(moduleField.field, moduleField.label));
    }
  }

  generateModulesOptions(): Select[] {
    if (!this.objAvailableModules) {
      return [];
    }

    let arModules: string[] = Object.keys(this.objAvailableModules);
    arModules.sort();
    return arModules.map(strKey => new Select(strKey, strKey));
  }

  _buildUsableContentFields(objectData: Array<Select>): void {
    if (blank(objectData)) return;

    const options: FieldmagicDropdownOption<WorkflowContentUsableField>[] = [];
    const uniqueIds = new Set<string>();

    for (const field of objectData) {
      if (data_get(field, 'extras.type') !== 'relate') {
        //FC-5037: customer will show name, firstname or lastname fields only when they have values on the record data
        const recordData = data_get(this.data, `record_data.${field.id}`);
        if(this.moduleName == 'customers'
          && ['first_name', 'last_name', 'name'].includes(field.id)
          && blank(recordData)) {
          continue;
        }
        this._addSimpleField(field, options, uniqueIds);
      }

      this._addRelatedFields(field, options, uniqueIds);
    }
    this.fields$.next(options.filter(option => !option.id.startsWith(`${str_singular(this.moduleName)}_asset`)));
  }

  onBlur = () => this.closeVar.emit();

  private _addSimpleField(
      field: Select,
      options: FieldmagicDropdownOption<WorkflowContentUsableField>[],
      uniqueIds: Set<string>
    ): void {
      if (uniqueIds.has(field.id)) return;

      options.push({
        id: field.id,
        text: field.text,
        type: fallback(data_get(this.data, 'extras.type'), { fallback: () => 'text' }),
        module: data_get(this.data, 'extras.module'),
        groupName: str_singular(this.data) || str_singular(this.moduleName)
      });

      uniqueIds.add(field.id);
    }

    private _addRelatedFields(
      field: Select,
      options: FieldmagicDropdownOption<WorkflowContentUsableField>[],
      uniqueIds: Set<string>
    ): void {
      const moduleField = data_get(field, 'extras.module');
      if (blank(moduleField) || moduleField === this.data || moduleField === this.moduleName) return;

      const usableFields = data_get(EMAIL_RELATED_MODULE_CONTENT_USABLE_FIELDS, moduleField);
      if (blank(usableFields)) return;

      const wrappingName = this._identifyWrappingVariableName(moduleField);
      let groupName = str_singular(moduleField) || this.moduleName;

      if (groupName === 'customer' && ['supplier_invoices', 'purchase_orders'].includes(this.data['module'])) {
        groupName = 'supplier';
      }

      for (const field of usableFields) {
        const [text, attributeName] = isArray(field) ? field : [field, field];
        const id = spf('%s_%s', { args: [wrappingName, attributeName] });

        if (uniqueIds.has(id)) {
          continue;
        }

        //FC-5037: customer group will show firstname or lastname fields only when they have values on the record data
        const recordData = data_get(this.data, `record_data.${id}`);
        if(groupName === 'customer'
            && ['customer_first_name', 'customer_last_name'].includes(id)
            && blank(recordData)) {
          continue;
        }

        options.push({
          id,
          text,
          type: fallback(data_get(this.data, 'extras.type'), { fallback: () => 'text' }),
          module: moduleField,
          groupName
        });

        uniqueIds.add(id);
      }
  }

  onFieldChange(fieldData: LooseObject): void {
    this.strField = fieldData.text;
    if (!isNil(this.strField) || this.quill.quillEditor) {
      this.strVariable = ` {{ ${this.strField} }} `;
      // Get the current position of the cursor, otherwise the field variable will be appended to a new line
      let cursorPosition: Range = this.quill.quillEditor.getSelection(true);
      // Update the editor message body
      this.quill.quillEditor.insertText(cursorPosition, this.strVariable);
    } else {
      this.notificationService.notifyWarning('select_field');
    }
  }

  private _identifyWrappingVariableName(moduleName: string): string {
    const alias = data_get(EMAIL_RELATED_MODULE_CONTENT_USABLE_FIELD_ALIASES, moduleName);

    if (blank(alias)) {
      return str_singular(moduleName);
    }

    if (isString(alias)) {
      return alias;
    }

    const appliedFor: string[] = data_get(alias, 'for');

    if (blank(appliedFor) || ! appliedFor.includes(data_get(this.data, 'module'))) {
      return str_singular(moduleName);
    }

    return fallback(data_get(alias, 'alias'), {
      fallback: () => str_singular(moduleName),
    });
  }
}

interface ModuleField {
  field: string,
  label: string
}

type ConfigureEmailDialogProps = {
  module: string;
  all_module_fields: Select[];
  contact_roles_list: Record<string, {
    config: Select[],
  }>;
  parent_module: Select[];
}